/**
 * System Chart Presets for Workflow Insights (canned Dimension/Measure/filter
 * configs). Distinct from Workflow Views — product-authored templates.
 */

import { CustomOperatorValue } from './report';
import { WORKFLOW_DATE_FILTER_FIELDS, WORKFLOW_FILTER_FIELDS } from './workflow-filter-fields';
import type { WorkflowDateFilterFieldName } from './workflow-filter-fields';
import {
  DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
  DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE,
  MAX_WORKFLOW_CHART_DIMENSIONS,
  parseWorkflowViewState,
  WORKFLOW_CHART_DATE_BUCKETS,
  WORKFLOW_CHART_DIMENSIONS,
  WORKFLOW_CHART_GRAINS,
  WORKFLOW_CHART_MEASURES,
  WORKFLOW_CHART_TYPES,
} from './workflow-insights';
import type {
  WorkflowChartSeriesRequestInput,
  WorkflowChartType,
  WorkflowViewState,
} from './workflow-insights';
import {
  WORKFLOW_RELATIVE_DATE_OPERATORS,
  WORKFLOW_RELATIVE_DATE_UNITS,
} from './workflow-relative-date';
import type {
  WorkflowRelativeDateOperator,
  WorkflowRelativeDateUnit,
  WorkflowRelativeDateValue,
} from './workflow-relative-date';
import { GQL } from '../graphql-exports';

/**
 * These string values are durable identifiers, not labels. They are stored as
 * `InsightPin.sourceSystemPresetId` and as entries in
 * `Org.hiddenInsightPresetIds`. Renaming a value orphans both: the org's
 * editable copy no longer matches the preset, and a hidden preset reappears
 * because the stored id is no longer in this map. Change display `name` on
 * the preset object instead.
 */
export const WORKFLOW_SYSTEM_CHART_PRESET_IDS = {
  completedWeeklyByType: 'system-completed-weekly-by-type',
  createdCompletedWeekly: 'system-created-completed-weekly',
  createdWeeklyByType: 'system-created-weekly-by-type',
  openByAssigneePriority: 'system-open-by-assignee-priority',
  openByStatusPriority: 'system-open-by-status-priority',
  openByWorkflowType: 'system-open-by-workflow-type',
  openHighPriorityByAssignee: 'system-open-high-priority-by-assignee',
  openOlderThan90Days: 'system-open-older-than-90-days',
  typicalCompletionByType: 'system-typical-completion-by-type',
  typicalCompletionByTypeMonthly: 'system-typical-completion-by-type-monthly',
  typicalTimeInStatus: 'system-typical-time-in-status',
  typicalTimeInStatusMonthly: 'system-typical-time-in-status-monthly',
} as const;

export type WorkflowSystemChartPresetId =
  (typeof WORKFLOW_SYSTEM_CHART_PRESET_IDS)[keyof typeof WORKFLOW_SYSTEM_CHART_PRESET_IDS];

/**
 * Retired IDs remain reserved because they may still exist in persisted Pins
 * and hidden-preset lists. Never reuse these strings for another preset.
 */
export const RETIRED_WORKFLOW_SYSTEM_CHART_PRESET_IDS = {
  typicalTimeInPhase: 'system-typical-time-in-phase',
} as const;

export type RetiredWorkflowSystemChartPresetId =
  (typeof RETIRED_WORKFLOW_SYSTEM_CHART_PRESET_IDS)[keyof typeof RETIRED_WORKFLOW_SYSTEM_CHART_PRESET_IDS];

export type SystemChartWorkflowFilter = {
  completed: boolean;
};

type WorkflowSystemRelativeDateFilterItem = {
  field: WorkflowDateFilterFieldName;
  operator: WorkflowRelativeDateOperator;
  value: WorkflowRelativeDateValue;
};

type WorkflowSystemColumnCategoryFilterItem = {
  field: typeof WORKFLOW_FILTER_FIELDS.columnCategory;
  operator: 'isNoneOf';
  value: GQL.ColumnCategory[];
};

type WorkflowSystemPriorityFilterItem = {
  field: typeof WORKFLOW_FILTER_FIELDS.priority;
  operator: CustomOperatorValue.WorkflowPriority;
  value: GQL.WorkflowPriority[];
};

export type WorkflowSystemChartFilterModel = {
  items: (
    | WorkflowSystemColumnCategoryFilterItem
    | WorkflowSystemPriorityFilterItem
    | WorkflowSystemRelativeDateFilterItem
  )[];
};

type WorkflowChartSeriesValueInput = NonNullable<WorkflowChartSeriesRequestInput['values']>[number];

type WorkflowSystemChartSeriesValue = Omit<WorkflowChartSeriesValueInput, 'filterModel'> & {
  filterModel?: WorkflowSystemChartFilterModel;
};

export type WorkflowSystemChartSeriesRequest = Omit<
  WorkflowChartSeriesRequestInput,
  'filterModel' | 'values'
> & {
  filterModel?: WorkflowSystemChartFilterModel;
  values?: WorkflowSystemChartSeriesValue[];
};

export type WorkflowSystemChartPreset = {
  chartType: WorkflowChartType;
  description: string;
  id: WorkflowSystemChartPresetId;
  name: string;
  seriesRequest: WorkflowSystemChartSeriesRequest;
  workflowFilter?: SystemChartWorkflowFilter;
};

const OPEN_WIP_FILTER = { completed: false } as const satisfies SystemChartWorkflowFilter;
const COMPLETED_FILTER = { completed: true } as const satisfies SystemChartWorkflowFilter;

function relativeDateFilterModel(
  field: WorkflowDateFilterFieldName,
  operator: WorkflowRelativeDateOperator,
  amount: number,
  unit: WorkflowRelativeDateUnit
): WorkflowSystemChartFilterModel {
  return {
    items: [{ field, operator, value: { amount, unit } }],
  };
}

const LAST_TWELVE_WEEKS_CREATED = relativeDateFilterModel(
  WORKFLOW_DATE_FILTER_FIELDS.createdAt,
  WORKFLOW_RELATIVE_DATE_OPERATORS.inTheLast,
  12,
  WORKFLOW_RELATIVE_DATE_UNITS.week
);
const LAST_TWELVE_WEEKS_COMPLETED = relativeDateFilterModel(
  WORKFLOW_DATE_FILTER_FIELDS.completedAt,
  WORKFLOW_RELATIVE_DATE_OPERATORS.inTheLast,
  12,
  WORKFLOW_RELATIVE_DATE_UNITS.week
);
const LAST_SIX_MONTHS_COMPLETED = relativeDateFilterModel(
  WORKFLOW_DATE_FILTER_FIELDS.completedAt,
  WORKFLOW_RELATIVE_DATE_OPERATORS.inTheLast,
  6,
  WORKFLOW_RELATIVE_DATE_UNITS.month
);
const OPEN_OLDER_THAN_90_DAYS = {
  items: [
    {
      field: WORKFLOW_DATE_FILTER_FIELDS.startedAt,
      operator: WORKFLOW_RELATIVE_DATE_OPERATORS.olderThan,
      value: { amount: 90, unit: WORKFLOW_RELATIVE_DATE_UNITS.day },
    },
    {
      field: WORKFLOW_FILTER_FIELDS.columnCategory,
      operator: 'isNoneOf',
      value: [GQL.ColumnCategory.Cancelled],
    },
  ],
} satisfies WorkflowSystemChartFilterModel;
const HIGH_PRIORITY_OPEN = {
  items: [
    {
      field: WORKFLOW_FILTER_FIELDS.priority,
      operator: CustomOperatorValue.WorkflowPriority,
      value: [GQL.WorkflowPriority.High, GQL.WorkflowPriority.Urgent],
    },
  ],
} satisfies WorkflowSystemChartFilterModel;

export const WORKFLOW_SYSTEM_CHART_PRESETS = [
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Open workflows grouped by workflow type.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByWorkflowType,
    name: 'Open workflows by workflow type',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.workflowType],
      measure: WORKFLOW_CHART_MEASURES.count,
    },
    workflowFilter: OPEN_WIP_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Open workflows per assignee, stacked by priority.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByAssigneePriority,
    name: 'Open workflows by assignee',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.assignee, WORKFLOW_CHART_DIMENSIONS.priority],
      measure: WORKFLOW_CHART_MEASURES.count,
    },
    workflowFilter: OPEN_WIP_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Open workflows older than 90 days, grouped by assignee.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.openOlderThan90Days,
    name: 'Workflows open more than 90 days',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.assignee],
      filterModel: OPEN_OLDER_THAN_90_DAYS,
      measure: WORKFLOW_CHART_MEASURES.count,
    },
    workflowFilter: OPEN_WIP_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.line,
    description: 'Workflows created and completed each week for the last 12 weeks.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.createdCompletedWeekly,
    name: 'Workflows created and completed each week',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.createdAt],
      measure: WORKFLOW_CHART_MEASURES.count,
      values: [
        {
          dateDimension: WORKFLOW_CHART_DIMENSIONS.createdAt,
          filterModel: LAST_TWELVE_WEEKS_CREATED,
          id: 'created',
          label: 'Created',
          measure: WORKFLOW_CHART_MEASURES.count,
        },
        {
          dateDimension: WORKFLOW_CHART_DIMENSIONS.completedAt,
          filterModel: LAST_TWELVE_WEEKS_COMPLETED,
          id: 'completed',
          label: 'Completed',
          measure: WORKFLOW_CHART_MEASURES.count,
        },
      ],
    },
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Typical completion time by workflow type over the last 6 months.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalCompletionByType,
    name: 'Typical completion time by workflow type',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.workflowType],
      filterModel: LAST_SIX_MONTHS_COMPLETED,
      measure: WORKFLOW_CHART_MEASURES.medianDurationDays,
      minSampleSize: DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE,
    },
    workflowFilter: COMPLETED_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.line,
    description: 'Typical completion time by workflow type each month for the last 6 months.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalCompletionByTypeMonthly,
    name: 'Typical completion time by workflow type each month',
    seriesRequest: {
      dateBucket: WORKFLOW_CHART_DATE_BUCKETS.month,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.completedAt, WORKFLOW_CHART_DIMENSIONS.workflowType],
      filterModel: LAST_SIX_MONTHS_COMPLETED,
      measure: WORKFLOW_CHART_MEASURES.medianDurationDays,
      minSampleSize: DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE,
    },
    workflowFilter: COMPLETED_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Typical time in each workflow status over the last 6 months.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalTimeInStatus,
    name: 'Typical time in each workflow status',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.column],
      filterModel: LAST_SIX_MONTHS_COMPLETED,
      grain: WORKFLOW_CHART_GRAINS.columnInterval,
      measure: WORKFLOW_CHART_MEASURES.medianDurationDays,
      minSampleSize: DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE,
    },
  },
  {
    chartType: WORKFLOW_CHART_TYPES.line,
    description: 'Typical time in each workflow status each month for the last 6 months.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalTimeInStatusMonthly,
    name: 'Typical time in each workflow status each month',
    seriesRequest: {
      dateBucket: WORKFLOW_CHART_DATE_BUCKETS.month,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.completedAt, WORKFLOW_CHART_DIMENSIONS.column],
      filterModel: LAST_SIX_MONTHS_COMPLETED,
      grain: WORKFLOW_CHART_GRAINS.columnInterval,
      measure: WORKFLOW_CHART_MEASURES.medianDurationDays,
      minSampleSize: DEFAULT_WORKFLOW_CHART_MIN_SAMPLE_SIZE,
    },
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Open workflows by status, stacked by priority.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByStatusPriority,
    name: 'Open workflows by status',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.column, WORKFLOW_CHART_DIMENSIONS.priority],
      measure: WORKFLOW_CHART_MEASURES.count,
    },
    workflowFilter: OPEN_WIP_FILTER,
  },
  {
    chartType: WORKFLOW_CHART_TYPES.column,
    description: 'Workflows created each week by workflow type for the last 12 weeks.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.createdWeeklyByType,
    name: 'Workflows created each week by workflow type',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.createdAt, WORKFLOW_CHART_DIMENSIONS.workflowType],
      filterModel: LAST_TWELVE_WEEKS_CREATED,
      measure: WORKFLOW_CHART_MEASURES.count,
    },
  },
  {
    chartType: WORKFLOW_CHART_TYPES.column,
    description: 'Workflows completed each week by workflow type for the last 12 weeks.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.completedWeeklyByType,
    name: 'Workflows completed each week by workflow type',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.completedAt, WORKFLOW_CHART_DIMENSIONS.workflowType],
      filterModel: LAST_TWELVE_WEEKS_COMPLETED,
      measure: WORKFLOW_CHART_MEASURES.count,
    },
  },
  {
    chartType: WORKFLOW_CHART_TYPES.bar,
    description: 'Open high-priority workflows by assignee.',
    id: WORKFLOW_SYSTEM_CHART_PRESET_IDS.openHighPriorityByAssignee,
    name: 'Open high-priority workflows by assignee',
    seriesRequest: {
      dateBucket: DEFAULT_WORKFLOW_CHART_DATE_BUCKET,
      dimensions: [WORKFLOW_CHART_DIMENSIONS.assignee],
      filterModel: HIGH_PRIORITY_OPEN,
      measure: WORKFLOW_CHART_MEASURES.count,
    },
    workflowFilter: OPEN_WIP_FILTER,
  },
] as const satisfies readonly WorkflowSystemChartPreset[];

export const DEFAULT_ORG_INSIGHT_SYSTEM_CHART_PRESET_IDS = [
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByWorkflowType,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByAssigneePriority,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.openOlderThan90Days,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.createdCompletedWeekly,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalCompletionByType,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalCompletionByTypeMonthly,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalTimeInStatus,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.typicalTimeInStatusMonthly,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.openByStatusPriority,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.createdWeeklyByType,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.completedWeeklyByType,
  WORKFLOW_SYSTEM_CHART_PRESET_IDS.openHighPriorityByAssignee,
] as const satisfies readonly WorkflowSystemChartPresetId[];

export function getWorkflowSystemChartPreset(
  id: WorkflowSystemChartPresetId
): WorkflowSystemChartPreset {
  const preset = WORKFLOW_SYSTEM_CHART_PRESETS.find(entry => entry.id === id);
  if (!preset) {
    throw new Error(`Unknown System Chart Preset: ${id}`);
  }
  return preset;
}

export function getIsWorkflowSystemChartPresetId(
  value: string
): value is WorkflowSystemChartPresetId {
  return WORKFLOW_SYSTEM_CHART_PRESETS.some(preset => preset.id === value);
}

export function getIsRetiredWorkflowSystemChartPresetId(
  value: string
): value is RetiredWorkflowSystemChartPresetId {
  return Object.values(RETIRED_WORKFLOW_SYSTEM_CHART_PRESET_IDS).some(id => id === value);
}

export function getIsDefaultOrgInsightSystemChartPresetId(
  value: string
): value is (typeof DEFAULT_ORG_INSIGHT_SYSTEM_CHART_PRESET_IDS)[number] {
  return DEFAULT_ORG_INSIGHT_SYSTEM_CHART_PRESET_IDS.some(id => id === value);
}

export function getDefaultOrgInsightSystemChartPresets(): WorkflowSystemChartPreset[] {
  return DEFAULT_ORG_INSIGHT_SYSTEM_CHART_PRESET_IDS.map(id => getWorkflowSystemChartPreset(id));
}

export function getWorkflowViewStateFromSystemChartPreset(preset: WorkflowSystemChartPreset): {
  chartId: WorkflowSystemChartPresetId;
  state: WorkflowViewState;
} {
  const chartId = preset.id;
  const insightValues = preset.seriesRequest.values;
  const state = parseWorkflowViewState({
    chartsIntegration: {
      activeChartId: chartId,
      charts: {
        [chartId]: {
          chartType: preset.chartType,
          dateBucket: preset.seriesRequest.dateBucket,
          dimensions: [...preset.seriesRequest.dimensions],
          grain: preset.seriesRequest.grain,
          insightValues,
          minSampleSize: preset.seriesRequest.minSampleSize,
          title: preset.name,
          values: [{ field: preset.seriesRequest.measure }],
        },
      },
    },
    filter: preset.seriesRequest.filterModel
      ? { filterModel: preset.seriesRequest.filterModel }
      : undefined,
    rowGrouping: {
      model: preset.seriesRequest.dimensions.slice(0, MAX_WORKFLOW_CHART_DIMENSIONS),
    },
    workflowFilter: preset.workflowFilter,
  });

  return { chartId, state };
}
